import { useState, useEffect } from 'react'
import { AlertTriangle, Info, AlertOctagon, Plus, Trash2, Loader2, MessageSquareWarning, X } from 'lucide-react'
import { coverageApi } from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { showToast } from '@/store/uiStore'

interface Insight {
  id: number
  country_code: string | null
  vendor: string | null
  data_source_header: string | null
  severity: 'info' | 'warning' | 'critical'
  title: string
  detail: string
  tags: string[]
  created_by_name: string | null
  created_at: string
}

const SEVERITY_CONFIG = {
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', label: 'Info' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', label: 'Warning' },
  critical: { icon: AlertOctagon, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', label: 'Critical' },
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── Add Insight Modal ─────────────────────────────────────────────────

function AddInsightModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { user } = useAuthStore()
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')
  const [countryCode, setCountryCode] = useState('')
  const [vendor, setVendor] = useState('')
  const [severity, setSeverity] = useState<'info' | 'warning' | 'critical'>('warning')
  const [tagsText, setTagsText] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!title.trim() || !detail.trim()) {
      showToast('Title and detail are required', 'error')
      return
    }
    setSaving(true)
    const res = await coverageApi.createInsight({
      title: title.trim(),
      detail: detail.trim(),
      country_code: countryCode.toUpperCase() || undefined,
      vendor: vendor || undefined,
      severity,
      tags: tagsText.split(',').map(t => t.trim()).filter(Boolean),
      created_by: user?.email || undefined,
      created_by_name: user?.name || undefined,
    })
    if (res.success) {
      showToast('Insight added', 'success')
      onCreated()
      onClose()
    } else {
      showToast(res.error || 'Failed to save', 'error')
    }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-color)]">
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">Add Team Insight</h3>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase">Country Code</label>
              <input value={countryCode} onChange={e => setCountryCode(e.target.value)} placeholder="e.g. CN" maxLength={2}
                className="w-full mt-1 px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)] uppercase" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase">Vendor</label>
              <input value={vendor} onChange={e => setVendor(e.target.value)} placeholder="e.g. AsiaVerify"
                className="w-full mt-1 px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)]" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase">Severity</label>
              <select value={severity} onChange={e => setSeverity(e.target.value as 'info' | 'warning' | 'critical')}
                className="w-full mt-1 px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)]">
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase">Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Short summary of the insight"
              className="w-full mt-1 px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)]" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase">Detail *</label>
            <textarea value={detail} onChange={e => setDetail(e.target.value)} rows={4}
              placeholder="Full explanation — what's the issue, why it matters, what to do about it"
              className="w-full mt-1 px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)] resize-none" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase">Tags (comma-separated)</label>
            <input value={tagsText} onChange={e => setTagsText(e.target.value)} placeholder="e.g. ruleset, 2+2, dob, custom-config"
              className="w-full mt-1 px-2 py-1.5 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)]" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--border-color)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)]">Cancel</button>
          <button onClick={handleSave} disabled={saving || !title.trim() || !detail.trim()}
            className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-50">
            {saving ? 'Saving...' : 'Add Insight'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Insight Card ──────────────────────────────────────────────────────

function InsightCard({ insight, onDelete }: { insight: Insight; onDelete: (id: number) => void }) {
  const config = SEVERITY_CONFIG[insight.severity]
  const Icon = config.icon

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} p-3`}>
      <div className="flex items-start gap-2">
        <Icon size={16} className={`${config.color} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-xs font-semibold ${config.color}`}>{config.label}</span>
            {insight.country_code && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)] font-mono">{insight.country_code}</span>
            )}
            {insight.vendor && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">{insight.vendor}</span>
            )}
          </div>
          <p className="text-sm font-medium text-[var(--text-primary)]">{insight.title}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1 whitespace-pre-line">{insight.detail}</p>
          <div className="flex items-center gap-3 mt-2 text-[10px] text-[var(--text-muted)]">
            {insight.tags.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {insight.tags.map((t, i) => (
                  <span key={i} className="px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)]">{t}</span>
                ))}
              </div>
            )}
            {insight.created_by_name && <span>by {insight.created_by_name}</span>}
            <span>{formatDate(insight.created_at)}</span>
          </div>
        </div>
        <button onClick={() => onDelete(insight.id)} className="p-1 text-[var(--text-muted)] hover:text-red-400 flex-shrink-0">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────

export function TeamInsights() {
  const [insights, setInsights] = useState<Insight[]>([])
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)

  const load = async () => {
    const res = await coverageApi.getInsights()
    if (res.success && res.data) {
      // Handle double-wrapping
      const data = (res.data as any)?.data || res.data
      setInsights(Array.isArray(data) ? data as Insight[] : [])
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const handleDelete = async (id: number) => {
    await coverageApi.deleteInsight(id)
    setInsights(insights.filter(i => i.id !== id))
    showToast('Insight removed', 'success')
  }

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquareWarning size={16} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Team Insights</h3>
          <span className="text-[10px] text-[var(--text-muted)]">
            {insights.filter(i => i.severity === 'critical').length} critical, {insights.filter(i => i.severity === 'warning').length} warnings
          </span>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-amber-600 text-white hover:bg-amber-500 transition-colors"
        >
          <Plus size={12} /> Add Insight
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 size={16} className="animate-spin text-[var(--text-muted)]" /></div>
      ) : insights.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] text-center py-4">No team insights yet. Add one to capture tribal knowledge about vendors, rulesets, or country-specific gotchas.</p>
      ) : (
        <div className="space-y-3">
          {insights.map(i => <InsightCard key={i.id} insight={i} onDelete={handleDelete} />)}
        </div>
      )}

      {addOpen && <AddInsightModal onClose={() => setAddOpen(false)} onCreated={load} />}
    </div>
  )
}
